// Tarush Agarwal's Portfolio
import { useEffect, useState } from 'react';
import { Helmet } from "react-helmet";
import About from './components/About';
import AIChat from './components/AIChat';
import Certifications from './components/Certifications';
import Contact from './components/Contact';
import Cursor from './components/Cursor';
import Experience from './components/Experience';
import Footer from './components/Footer';
import Hero from './components/Hero';
import Navbar from './components/Navbar';
import ProjectModal from './components/ProjectModal';
import Projects from './components/Projects';
import Roles from './components/Roles';
import Skills from './components/Skills';
import Resume from './components/Resume';
import { navLinks } from './constants';
import { ThemeProvider } from './context/ThemeContext';

// Main App Component with enhanced animations and gradients
export default function App() {
  const [navOpen, setNavOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [, setErrorModal] = useState({ isOpen: false, title: '', message: '' });
  const [scrollY, setScrollY] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  // Enhanced scroll handling with parallax
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);

      const sections = navLinks.map(link => document.getElementById(link.href.substring(1))).filter(Boolean);
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        if (section.offsetTop <= scrollPosition && section.offsetTop + section.offsetHeight > scrollPosition) {
          setActiveSection(section.id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleScrollTo = (href) => {
    document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
    setNavOpen(false);
  };

  const handleOpenModal = (project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProject(null);
  };

  return (
    <ThemeProvider>
      <div style={{
        minHeight: '100vh',
        lineHeight: 1.6,
        overflow: 'hidden auto'
      }} className="bg-dark-bg text-white selection:bg-primary-purple selection:text-white">
        <Helmet>
          <title>Tarush Agarwal | Portfolio</title>
          <meta name="description" content="Official portfolio of Tarush Agarwal. Explore projects, experience, and certifications in AI, Web Dev, and Cloud." />
          <meta name="keywords" content="Tarush Agarwal, Portfolio, AI, Cloud, React, Developer, VIT, Projects, Resume, Software Development" />
          <meta name="author" content="Tarush Agarwal" />
        </Helmet>

        {/* Custom Cursor */}
        {!isMobile && <Cursor />}

        {/* Midnight Background */}
        <div className="midnight-bg"></div>

        <Navbar
          navOpen={navOpen}
          setNavOpen={setNavOpen}
          activeSection={activeSection}
          handleScrollTo={handleScrollTo}
          scrollY={scrollY}
        />

        <Hero handleScrollTo={handleScrollTo} isMobile={isMobile} />
        <About />
        <Skills />
        <Projects handleOpenModal={handleOpenModal} />
        <Experience />
        <AIChat setErrorModal={setErrorModal} />
        <Certifications />
        <Roles />
        <Resume />
        <Contact />
        <Footer />

        <ProjectModal
          isModalOpen={isModalOpen}
          selectedProject={selectedProject}
          handleCloseModal={handleCloseModal}
        />
      </div>
    </ThemeProvider>
  );
}